import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantIPRestriction.css";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/tenant-ip-restrictions/`;

// Static parent FK — replace with the actual TenantSecuritySettings ID as needed
const STATIC_SECURITY_SETTINGS_ID = 1;

const emptyRestriction = {
  cidr_range: ""
};

const AddTenantIPRestriction = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editRestriction = location.state?.editRestriction || null;
  const isEditMode = Boolean(editRestriction?.id);

  const [restrictions, setRestrictions] = useState(() => {
    if (editRestriction) {
      return [
        {
          cidr_range: editRestriction.cidr_range || ""
        }
      ];
    }

    return [{ ...emptyRestriction }];
  });

  const [loading, setLoading] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setRestrictions((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [name]: value } : r))
    );
  };

  const addRestriction = () => {
    if (isEditMode) return;
    setRestrictions((prev) => [...prev, { ...emptyRestriction }]);
  };

  const removeRestriction = (index) => {
    if (isEditMode || restrictions.length === 1) return;
    setRestrictions((prev) => prev.filter((_, i) => i !== index));
  };

  // Basic CIDR sanity check (IPv4 only — backend enforces the real validation)
  const isValidCidr = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return false;

    // IPv4 CIDR
    const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/([0-9]|[12][0-9]|3[0-2]))$/;
    // IPv4 single address
    const ipv4Host = /^(\d{1,3}\.){3}\d{1,3}$/;
    // IPv6 (rough)
    const ipv6 = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}(\/\d{1,3})?$/;

    if (ipv4.test(trimmed) || ipv4Host.test(trimmed) || ipv6.test(trimmed)) {
      if (ipv4Host.test(trimmed)) {
        return trimmed
          .split(".")
          .every((octet) => Number(octet) >= 0 && Number(octet) <= 255);
      }
      return true;
    }
    return false;
  };

  const validateRow = (r, index) => {
    const label = `IP Restriction ${index + 1}`;

    if (!r.cidr_range.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide a CIDR range for ${label}.`
      });
      return false;
    }

    if (r.cidr_range.trim().length > 100) {
      Swal.fire({
        icon: "warning",
        title: "CIDR Range Too Long",
        text: `CIDR range for ${label} must be 100 characters or fewer.`
      });
      return false;
    }

    if (!isValidCidr(r.cidr_range)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid CIDR Range",
        text: `CIDR range for ${label} is invalid. Use a valid IPv4 or IPv6 address/range (e.g. 192.168.1.0/24).`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (r, tenantUser) => ({
    security_settings: STATIC_SECURITY_SETTINGS_ID,
    cidr_range: r.cidr_range.trim(),
    tenant: tenantUser.tenant_id,
    user: tenantUser.user_id
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id || !tenantUser.user_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID or User ID is missing from tenant_user."
      });
      return;
    }

    for (let i = 0; i < restrictions.length; i++) {
      if (!validateRow(restrictions[i], i)) return;
    }

    // Duplicate check within the form
    const values = restrictions.map((r) => r.cidr_range.trim().toLowerCase());
    const duplicateIndex = values.findIndex((v, i) => values.indexOf(v) !== i);

    if (duplicateIndex !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate CIDR Range",
        text: `CIDR range "${restrictions[
          duplicateIndex
        ].cidr_range.trim()}" is used more than once.`
      });
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const payload = buildPayload(restrictions[0], tenantUser);

        console.log("Update Tenant IP Restriction Payload:", payload);

        const response = await axios.patch(
          `${API_URL}${editRestriction.id}/`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        console.log("Update Tenant IP Restriction Response:", response.data);

        await Swal.fire({
          icon: "success",
          title: "IP Restriction Updated Successfully",
          text: "Tenant IP restriction has been updated successfully.",
          confirmButtonText: "OK"
        });

        navigate("/admin/ip-restrictions");
        return;
      }

      const payload = restrictions.map((r) => buildPayload(r, tenantUser));

      console.log("Create Tenant IP Restrictions Payload:", payload);

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" }
      });

      console.log("Create Tenant IP Restrictions Response:", response.data);

      await Swal.fire({
        icon: "success",
        title: "IP Restrictions Created Successfully",
        text: `${restrictions.length} IP restriction${
          restrictions.length > 1 ? "s" : ""
        } created successfully.`,
        confirmButtonText: "OK"
      });

      navigate("/admin/ip-restrictions");
    } catch (error) {
      console.error(
        isEditMode
          ? "Update Tenant IP Restriction Error:"
          : "Create Tenant IP Restrictions Error:",
        error
      );

      let errorMessage = isEditMode
        ? "Unable to update tenant IP restriction."
        : "Unable to create tenant IP restrictions.";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          errorMessage = Object.entries(error.response.data.errors)
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors) ? errors.join(", ") : errors
                }`
            )
            .join(" | ");
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      Swal.fire({
        icon: "error",
        title: isEditMode ? "Update Failed" : "Save Failed",
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/ip-restrictions");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-ip-page">
          <div className="tenant-ip-page-header">
            <div>
              <h2>
                {isEditMode ? "Edit IP Restriction" : "Create IP Restriction"}
              </h2>
              <p>
                {isEditMode
                  ? "Update allowed IP range information."
                  : "Add one or multiple allowed IP ranges for the tenant."}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="tenant-ip-card">
              <div className="tenant-ip-form-body">
                {restrictions.map((r, index) => (
                  <div className="tenant-ip-record" key={index}>
                    <div className="tenant-ip-record-header">
                      <div>
                        <h4>
                          {isEditMode
                            ? "IP Restriction Details"
                            : `IP Restriction ${index + 1}`}
                        </h4>
                      </div>

                      {!isEditMode && restrictions.length > 1 && (
                        <button
                          type="button"
                          className="tenant-ip-remove-btn"
                          onClick={() => removeRestriction(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="tenant-ip-form-grid">
                      <div className="tenant-ip-form-group">
                        <label>
                          CIDR Range <span>*</span>
                        </label>
                        <input
                          type="text"
                          name="cidr_range"
                          value={r.cidr_range}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="e.g. 192.168.1.0/24"
                          maxLength={100}
                          required
                        />
                        <p className="tenant-ip-hint">
                          IPv4 or IPv6 address/CIDR range. Multiple rows are
                          allowed (e.g. head office + site offices).
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditMode && (
                  <button
                    type="button"
                    className="tenant-ip-add-btn"
                    onClick={addRestriction}
                  >
                    <span>+</span>
                    Add Another IP Restriction
                  </button>
                )}
              </div>
            </div>

            <div className="tenant-ip-form-actions">
              <button
                type="button"
                className="tenant-ip-cancel-btn"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="tenant-ip-save-btn"
                disabled={loading}
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Saving..."
                  : isEditMode
                  ? "Update IP Restriction"
                  : `Save ${restrictions.length} IP Restriction${
                      restrictions.length > 1 ? "s" : ""
                    }`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTenantIPRestriction;